import { useEffect, useMemo } from "react";

import { useLaborFormulas } from "../../../hooks/query";
import { calculateLaborQuantities } from "../utils/calculateLaborQuantities";
import type { LaborFormulaFlag } from "../types";
import type { LaborRowItem } from "../types";
import { useCalculationScope } from "./useCalculationScope";
import { useEstimateContractContextManager } from "./";

export function useLaborEvaluator(laborRows: LaborRowItem[]) {
    const { setValues } = useEstimateContractContextManager();
    const { scope, loading: scopeLoading } = useCalculationScope();
    const { useList } = useLaborFormulas();
    const {
        data: formulas,
        isLoading: formulasLoading,
        isError,
        error,
    } = useList();

    const ready = !scopeLoading && !formulasLoading && !!formulas;

    const { quantities, formulaInfo } = useMemo(() => {
        if (!ready) {
            return { quantities: {}, formulaInfo: {} };
        }
        return calculateLaborQuantities(scope, formulas, laborRows);
    }, [ready, scope, formulas, laborRows]);

    const syncLaborQuantities = () => {
        if (!Object.keys(quantities).length) return;

        setValues((prev: any) => {
            const labor = { ...prev.labor };
            let changed = false;

            for (const row of laborRows) {
                const qty = quantities[row.id];
                if (qty === undefined) continue;

                const current = labor[row.id];
                if (
                    !current ||
                    current.quantityOverridden ||
                    Number(current.quantity) === Number(qty)
                ) {
                    continue;
                }

                changed = true;
                labor[row.id] = {
                    ...current,
                    quantity: qty as number,
                };
            }

            return changed ? { ...prev, labor } : prev;
        });
    };

    useEffect(() => {
        syncLaborQuantities();
    }, [quantities, laborRows, setValues]);

    return {
        quantities,
        formulaFlags: formulaInfo as Record<string, LaborFormulaFlag>,
        ready,
        loading: scopeLoading || formulasLoading,
        error: isError
            ? (error as Error)?.message || "Failed to load labor formulas"
            : null,
    };
}
